<script lang="ts" setup>
import { FontAwesomeIcon } from '@shared/plugin/icons';
import { computed, ref, useSlots } from 'vue';
import type { FontAwesomeIconProps } from '@fortawesome/vue-fontawesome';
import VSpinner from '@shared/component/VSpinner';
import VModal from '../VModal/VModal.vue';
import { ModalType } from '../types';
import { type ButtonType } from './vButtonTypes';

export interface VButtonProps {
  ariaLabel?: string;
  iconOnlyResponsiveness?: boolean;
  type?: ButtonType;
  inverted?: boolean;
  prependIcon?: FontAwesomeIconProps['icon'];
  appendIcon?: FontAwesomeIconProps['icon'];
  loading?: boolean;
  disabled?: boolean;
  confirmDialog?: boolean;
  confirmDialogText?: string;
  confirmDialogButtonText?: string;
  submit?: boolean;
  href?: string;
  hideShadow?: boolean;
  iconOnly?: boolean;
  fontSize?: 'xs' | 'sm' | 'lg' | 'xl';
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  align?: 'left' | 'center' | 'right';
  progress?: number;
  alt?: string;
  rel?: string;
  target?: string;
}

const props = withDefaults(defineProps<VButtonProps>(), {
  type: 'primary',
  hideShadow: false,
  size: 'md',
  align: 'center',
  iconOnly: false,
  alt: undefined,
});
const emit = defineEmits<{ click: [value?: Event] }>();
const slots = useSlots();

defineOptions({
  inheritAttrs: false,
});

const modalOpen = ref(false);
const progressValue = computed(() =>
  props.progress ? Math.max(0, Math.min(100, props.progress)) : props.progress,
);

const showProgressbar = computed(() => props.loading && progressValue.value !== undefined);
const progressBackgroundPosition = computed(() => 100 - (progressValue.value ?? 0));

const modalType = computed(() => {
  switch (props.type) {
    case 'danger':
      return ModalType.Danger;
    default:
      return ModalType.Success;
  }
});

const spinnerType = computed(() => {
  if (props.type === 'danger') return 'danger';
  if (props.disabled) return 'white';
  return 'primary';
});

function onClick(event: Event) {
  if (props.loading) return;
  if (!props.confirmDialog) {
    emit('click', event);
    return;
  }
  if (modalOpen.value) {
    emit('click', event);
    modalOpen.value = false;
  } else {
    modalOpen.value = true;
  }
}

const justify = {
  left: 'justify-start',
  center: 'justify-center',
  right: 'justify-end',
};

const buttonClasses = computed(() => {
  let baseClasses = `relative inline-flex items-center align-top ${
    justify[props.align ?? 'left']
  } font-sans box-border cursor-pointer border rounded font-base font-bold leading-normal outline-none transition-[background-color,color] duration-[0.3s] no-underline`;
  const groupClasses =
    'group-[]/v-button-group--attached:grow group-[]/v-button-group--attached:justify-center group-[]/v-multiple-choice-buttons:rounded-none group-[]/v-multiple-choice-buttons:border-l-0 group-[]/v-multiple-choice-buttons:border-t-0 group-[]/v-multiple-choice-buttons:last:rounded-br group-[]/v-button-group--attached:not-first:rounded-l-none group-[]/v-button-group--attached:not-last:rounded-r-none';
  const typeClasses = {
    primary: 'bg-primary-600 text-white hover:bg-primary-700 border-transparent',
    danger: 'bg-red text-white hover:bg-red-700 border-transparent',
    plain: 'bg-transparent text-black hover:bg-grey-100 border-transparent !px-3',
  };

  const disabledClasses = 'hover:cursor-no-drop border border-solid border-grey-200 bg-grey-200';
  const invertedClasses = {
    primary:
      'bg-white border border-solid border-grey-100 text-primary-500 hover:bg-grey-100 hover:text-primary-600',
    danger:
      'bg-white border border-solid border-grey-100 text-red hover:text-red-700 hover:bg-grey-100',
    plain: 'bg-transparent text-black hover:bg-grey-100 border-transparent !px-3',
  };

  if (!props.hideShadow) {
    baseClasses += ' shadow';
    typeClasses.primary += ' focus:shadow-sm focus:shadow-primary-300';
    typeClasses.danger += ' focus:shadow-sm focus:shadow-red-100';
  }

  if (props.iconOnly) {
    baseClasses = `relative inline-flex items-center justify-center align-top p-3 cursor-pointer border rounded font-base font-bold aspect-square transition-[background-color,color] duration-[0.3s]`;
    if (!props.hideShadow) {
      baseClasses += ' shadow';
    }
  }

  const classes = [baseClasses, groupClasses];
  if (props.disabled) {
    classes.push(disabledClasses);
  } else if (props.inverted) {
    classes.push(invertedClasses[props.type]);
  } else {
    classes.push(typeClasses[props.type]);
  }
  return classes.join(' ');
});

const fontSize = computed(() => {
  switch (props.fontSize) {
    case 'xs':
      return ' text-xs';
    case 'sm':
      return ' text-sm';
    case 'lg':
      return ' text-lg';
    case 'xl':
      return ' text-xl';
    default:
      return '';
  }
});

const sizeClasses = computed(() => {
  const base = props.iconOnly
    ? {
        xs: 'size-6',
        sm: 'size-8',
        md: 'size-11',
        lg: 'size-12',
        xl: 'size-14',
      }
    : {
        xs: 'h-7 px-4 py-3',
        sm: 'h-9 px-4 py-4',
        md: 'h-11 px-4 py-5',
        lg: 'h-12 px-4 py-6',
        xl: 'h-14 px-4 py-7',
      };

  return base[props.size ?? 'md'];
});
</script>

<template>
  <Component
    :aria-busy="props.loading"
    :is="href ? 'a' : 'button'"
    :href="href"
    @click="onClick"
    :disabled="disabled"
    :aria-label="props.ariaLabel"
    :alt="props.alt"
    :rel="props.rel"
    :target="props.target"
    :class="[
      buttonClasses,
      fontSize,
      sizeClasses,
      {
        'max-lg:px-[.6875rem]': props.iconOnlyResponsiveness,
        'bg-[linear-gradient(90deg,_rgb(from_theme(colors.primary.500)_r_g_b_/_10%)_50%,_transparent_50%)] bg-[length:200%_100%] transition-[background-position]':
          showProgressbar,
      },
    ]"
    v-bind="$attrs"
    :type="submit ? 'submit' : 'button'"
    :style="
      progress !== undefined ? { backgroundPosition: `${progressBackgroundPosition}%` } : null
    "
  >
    <VSpinner
      :variant="spinnerType"
      :class="{
        'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transform': !showProgressbar,
        'pl-1': showProgressbar,
      }"
      v-if="loading || showProgressbar"
    />

    <FontAwesomeIcon
      v-if="prependIcon && !showProgressbar"
      :class="{ 'opacity-0': loading, 'pr-2': !props.iconOnly && slots.default }"
      :icon="prependIcon"
      fixed-width
    />

    <span
      v-if="!props.iconOnly"
      :class="[
        {
          'max-lg:hidden': props.iconOnlyResponsiveness,
          'opacity-0': loading && !showProgressbar,
        },
      ]"
    >
      <slot></slot>
    </span>

    <FontAwesomeIcon
      v-if="appendIcon && !props.iconOnly"
      :class="{ 'opacity-0': loading, 'pl-2': !props.iconOnly && slots.default }"
      :icon="appendIcon"
      fixed-width
    />
  </Component>

  <VModal
    v-if="confirmDialog"
    :type="modalType"
    :is-active="modalOpen"
    :confirm-button-text="confirmDialogButtonText ?? 'Bevestigen'"
    :loading="loading"
    @confirm-action="(e: any) => onClick(e)"
    @close-modal="modalOpen = false"
    v-bind="$attrs"
  >
    {{ confirmDialogText ?? 'Weet je zeker dat je deze actie wil uitvoeren?' }}
  </VModal>
</template>
